import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Paper, Grid, useTheme, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tabs, Tab, CircularProgress, Alert } from '@mui/material';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import HistoryIcon from '@mui/icons-material/History';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import LanIcon from '@mui/icons-material/Lan';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../contexts/AuthContext';
import { MEMBER_TYPES } from '../types/User';
import { useWhoWeAreContent } from '../hooks/useWhoWeAreContent';
import { WhoWeAreContent } from '../types/Content';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './WhoWeAre.css';
import ResponsiveTitle from '../components/ResponsiveTitle';

// Configuration for ReactQuill
const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ['clean']
  ],
};

const quillFormats = [
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet'
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const WhoWeAre = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const isAdmin = user?.role === MEMBER_TYPES.ADMIN;
  const { content, loading, error, updateContent } = useWhoWeAreContent();

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editContent, setEditContent] = useState<WhoWeAreContent | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (content) {
      setEditContent(JSON.parse(JSON.stringify(content)));
    }
  }, [content]);

  const handleOpenEdit = () => {
    setEditContent(JSON.parse(JSON.stringify(content)));
    setTabValue(0);
    setOpenEditDialog(true);
  };

  const handleCloseEdit = () => {
    setOpenEditDialog(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!editContent) return;

    setSaving(true);
    setSaveError(null);
    try {
      await updateContent(editContent);
      setOpenEditDialog(false);
    } catch (err) {
      console.error("Error saving content:", err);
      setSaveError("Une erreur est survenue lors de la sauvegarde.");
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const updateField = (section: keyof WhoWeAreContent | 'root', field: string, value: any) => {
    if (!editContent) return;

    const newContent = { ...editContent };

    if (section === 'root') {
      (newContent as any)[field] = value;
    } else {
      (newContent as any)[section][field] = value;
    }

    setEditContent(newContent);
  };

  const updateCard = (index: number, value: string) => {
    if (!editContent) return;
    const newContent = { ...editContent };
    newContent.section5.cards[index] = value;
    setEditContent(newContent);
  };

  const Section = ({ title, icon, children }: { title: string, icon: React.ReactNode, children: React.ReactNode }) => (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        mb: 4,
        background: 'rgba(23, 42, 69, 0.8)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(74, 144, 226, 0.1)',
        borderRadius: 2,
        transition: 'transform 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          borderColor: 'rgba(74, 144, 226, 0.3)'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
        <Box sx={{
          p: 1,
          borderRadius: '50%',
          bgcolor: 'rgba(74, 144, 226, 0.1)',
          color: theme.palette.primary.main,
          display: 'flex'
        }}>
          {icon}
        </Box>
        <ResponsiveTitle variant="h4" component="h2" sx={{
          color: theme.palette.primary.main,
          fontWeight: 'bold',
          textShadow: '0 2px 4px rgba(0,0,0,0.3)'
        }}>
          {title}
        </ResponsiveTitle>
      </Box>
      <Box sx={{ pl: { xs: 0, md: 7 } }}>
        {children}
      </Box>
    </Paper>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 8, position: 'relative' }}>
      {isAdmin && (
        <Box sx={{ position: 'absolute', top: 20, right: 20, zIndex: 10 }}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<EditIcon />}
            onClick={handleOpenEdit}
          >
            Modifier la page
          </Button>
        </Box>
      )}

      <Box sx={{ textAlign: 'center', mb: 6, mt: isAdmin ? 4 : 0 }}>
        <ResponsiveTitle variant="h2" component="h1" gutterBottom sx={{
          fontWeight: 900,
          background: 'linear-gradient(45deg, #4a90e2 30%, #64a6ff 90%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb: 3
        }}>
          {content.heroTitle}
        </ResponsiveTitle>
      </Box>

      <Section title={content.section1.title} icon={<LanIcon fontSize="large" />}>
        <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
          <span dangerouslySetInnerHTML={{ __html: content.section1.text }} />
        </Typography>
      </Section>

      <Section title={content.section2.title} icon={<SportsEsportsIcon fontSize="large" />}>
        <Box sx={{ fontSize: '1.1rem', lineHeight: 1.8, '& p': { mb: 2 } }} dangerouslySetInnerHTML={{ __html: content.section2.text }} />
      </Section>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Section title={content.section3.title} icon={<HistoryIcon fontSize="large" />}>
            <Box sx={{ fontSize: '1.1rem', lineHeight: 1.8, '& p': { mb: 2 } }} dangerouslySetInnerHTML={{ __html: content.section3.text }} />
          </Section>
        </Grid>

        <Grid item xs={12} md={6}>
          <Section title={content.section4.title} icon={<LocationOnIcon fontSize="large" />}>
            <Typography variant="h6" gutterBottom color="primary">{content.section4.locationTitle}</Typography>
            <Typography paragraph sx={{ mb: 3 }} dangerouslySetInnerHTML={{ __html: content.section4.locationText }} />

            <Typography variant="h6" gutterBottom color="primary">{content.section4.equipmentTitle}</Typography>
            <Typography paragraph dangerouslySetInnerHTML={{ __html: content.section4.equipmentText }} />
          </Section>
        </Grid>
      </Grid>

      <Section title={content.section5.title} icon={<RocketLaunchIcon fontSize="large" />}>
        <Typography paragraph sx={{ fontSize: '1.2rem', fontWeight: 'bold', mb: 3 }} dangerouslySetInnerHTML={{ __html: content.section5.intro }} />

        <Grid container spacing={2}>
          {content.section5.cards.map((text, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Box sx={{
                p: 2,
                bgcolor: 'rgba(74, 144, 226, 0.1)',
                borderRadius: 2,
                textAlign: 'center',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>{text}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="h4" sx={{
            color: theme.palette.secondary.main,
            fontWeight: 'bold',
            fontStyle: 'italic'
          }} dangerouslySetInnerHTML={{ __html: content.section5.outro }} />
        </Box>
      </Section>

      {/* Edit Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={handleCloseEdit}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Modifier le contenu "Qui sommes-nous ?"
          <IconButton onClick={handleCloseEdit} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {editContent && (
            <Box sx={{ width: '100%' }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                  <Tab label="Entête" />
                  <Tab label="Section 1" />
                  <Tab label="Section 2" />
                  <Tab label="Section 3" />
                  <Tab label="Section 4" />
                  <Tab label="Section 5" />
                </Tabs>
              </Box>

              {saveError && <Alert severity="error" sx={{ mt: 2 }}>{saveError}</Alert>}

              <TabPanel value={tabValue} index={0}>
                <TextField
                  fullWidth
                  label="Titre Principal (Héro)"
                  value={editContent.heroTitle}
                  onChange={(e) => updateField('root', 'heroTitle', e.target.value)}
                  margin="normal"
                />
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <TextField
                  fullWidth
                  label="Titre Section 1"
                  value={editContent.section1.title}
                  onChange={(e) => updateField('section1', 'title', e.target.value)}
                  margin="normal"
                  sx={{ mb: 2 }}
                />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Contenu Section 1</Typography>
                <ReactQuill
                  theme="snow"
                  value={editContent.section1.text}
                  onChange={(val: string) => updateField('section1', 'text', val)}
                  modules={quillModules}
                  formats={quillFormats}
                />
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <TextField
                  fullWidth
                  label="Titre Section 2"
                  value={editContent.section2.title}
                  onChange={(e) => updateField('section2', 'title', e.target.value)}
                  margin="normal"
                  sx={{ mb: 2 }}
                />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Contenu Section 2</Typography>
                <ReactQuill
                  theme="snow"
                  value={editContent.section2.text}
                  onChange={(val: string) => updateField('section2', 'text', val)}
                  modules={quillModules}
                  formats={quillFormats}
                />
              </TabPanel>

              <TabPanel value={tabValue} index={3}>
                <TextField
                  fullWidth
                  label="Titre Section 3"
                  value={editContent.section3.title}
                  onChange={(e) => updateField('section3', 'title', e.target.value)}
                  margin="normal"
                  sx={{ mb: 2 }}
                />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Contenu Section 3</Typography>
                <ReactQuill
                  theme="snow"
                  value={editContent.section3.text}
                  onChange={(val: string) => updateField('section3', 'text', val)}
                  modules={quillModules}
                  formats={quillFormats}
                />
              </TabPanel>

              <TabPanel value={tabValue} index={4}>
                <TextField
                  fullWidth
                  label="Titre Principal Section 4"
                  value={editContent.section4.title}
                  onChange={(e) => updateField('section4', 'title', e.target.value)}
                  margin="normal"
                  sx={{ mb: 2 }}
                />

                <Box sx={{ mt: 3, mb: 2, border: '1px solid rgba(255,255,255,0.1)', p: 2, borderRadius: 1 }}>
                  <TextField
                    fullWidth
                    label="Titre Localisation"
                    value={editContent.section4.locationTitle}
                    onChange={(e) => updateField('section4', 'locationTitle', e.target.value)}
                    margin="normal"
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Texte Localisation</Typography>
                  <ReactQuill
                    theme="snow"
                    value={editContent.section4.locationText}
                    onChange={(val: string) => updateField('section4', 'locationText', val)}
                    modules={quillModules}
                    formats={quillFormats}
                  />
                </Box>

                <Box sx={{ mt: 3, mb: 2, border: '1px solid rgba(255,255,255,0.1)', p: 2, borderRadius: 1 }}>
                  <TextField
                    fullWidth
                    label="Titre Matériel"
                    value={editContent.section4.equipmentTitle}
                    onChange={(e) => updateField('section4', 'equipmentTitle', e.target.value)}
                    margin="normal"
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Texte Matériel</Typography>
                  <ReactQuill
                    theme="snow"
                    value={editContent.section4.equipmentText}
                    onChange={(val: string) => updateField('section4', 'equipmentText', val)}
                    modules={quillModules}
                    formats={quillFormats}
                  />
                </Box>
              </TabPanel>

              <TabPanel value={tabValue} index={5}>
                <TextField
                  fullWidth
                  label="Titre Section 5"
                  value={editContent.section5.title}
                  onChange={(e) => updateField('section5', 'title', e.target.value)}
                  margin="normal"
                  sx={{ mb: 2 }}
                />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>Introduction</Typography>
                <ReactQuill
                  theme="snow"
                  value={editContent.section5.intro}
                  onChange={(val: string) => updateField('section5', 'intro', val)}
                  modules={quillModules}
                  formats={quillFormats}
                />

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Cartes (Points forts)</Typography>
                  {editContent.section5.cards.map((cardText, index) => (
                    <TextField
                      key={index}
                      fullWidth
                      label={`Carte ${index + 1}`}
                      value={cardText}
                      onChange={(e) => updateCard(index, e.target.value)}
                      margin="normal"
                    />
                  ))}
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Phrase de fin</Typography>
                  <ReactQuill
                    theme="snow"
                    value={editContent.section5.outro}
                    onChange={(val: string) => updateField('section5', 'outro', val)}
                    modules={quillModules}
                    formats={quillFormats}
                  />
                </Box>
              </TabPanel>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseEdit} color="inherit" disabled={saving}>
            Annuler
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={saving}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default WhoWeAre;
